import { useQuery } from 'react-query';
import { useLocalStorage } from '../stores/localStorage';
import { BuildFoundryService } from '../api/BuildFoundryService';

// Hook for fetching BuildFoundry categories
export function useFetchBuildFoundryCategories() {
  const { data, error, isLoading } = useQuery(
    'buildFoundryCategories',
    async () => {
      const response = await BuildFoundryService.getCategories();
      return response;
    },
    {
      enabled: !!useLocalStorage.getState().sessionToken,
      staleTime: 300000, // 5 minutes - categories don't change often
      cacheTime: 600000, // 10 minutes
    }
  );

  return { data, error, isLoading };
}

// Hook for fetching BuildFoundry items with optional filters
export function useFetchBuildFoundryItems(
  categoryId?: string,
  page: number = 1,
  perPage: number = 20,
  search?: string
) {
  const { data, error, isLoading } = useQuery(
    ['buildFoundryItems', categoryId, page, perPage, search],
    async () => {
      const response = await BuildFoundryService.getFoundryItems(categoryId, page, perPage, search);
      return response;
    },
    {
      enabled: !!useLocalStorage.getState().sessionToken && !!categoryId,
      staleTime: 60000, // 1 minute - items might change more frequently
      cacheTime: 300000, // 5 minutes
      keepPreviousData: true, // Keep previous data while fetching new page
    }
  );

  return { data, error, isLoading };
}

// Hook for fetching BuildFoundry items by category ID only
export function useFetchBuildFoundryItemsByCategory(categoryId: string) {
  return useFetchBuildFoundryItems(categoryId, 1, 20);
}

// Hook for searching BuildFoundry items
export function useSearchBuildFoundryItems(searchTerm: string, categoryId?: string) {
  const { data, error, isLoading } = useQuery(
    ['buildFoundryItemsSearch', categoryId, searchTerm],
    async () => {
      const response = await BuildFoundryService.getFoundryItems(categoryId, 1, 20, searchTerm);
      return response;
    },
    {
      enabled: !!useLocalStorage.getState().sessionToken && !!searchTerm.trim(),
      staleTime: 60000, // 1 minute
      cacheTime: 300000, // 5 minutes
    }
  );

  return { data, error, isLoading };
}

// Hook for fetching BuildFoundry items by category path
export function useFetchBuildFoundryItemsByCategoryPath(categoryPath: string) {
  const { data, error, isLoading } = useQuery(
    ['buildFoundryItemsByCategoryPath', categoryPath],
    async () => {
      const response = await BuildFoundryService.getFoundryItemsByCategoryPath(categoryPath);
      return response;
    },
    {
      enabled: !!useLocalStorage.getState().sessionToken && !!categoryPath,
      staleTime: 60000, // 1 minute - items might change more frequently
      cacheTime: 300000, // 5 minutes
    }
  );

  return { data, error, isLoading };
}

// Hook for fetching BuildFoundry item by id
export function useFetchBuildFoundryItemById(itemId: string) {
  const { data, error, isLoading } = useQuery(['buildFoundryItemById', itemId], async () => {
    const response = await BuildFoundryService.getFoundryItemById(itemId);
    return response;
  });

  return { data, error, isLoading };
}
